import Close from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import type { Option } from '@openx/types/options';
import { type MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useFiltersContext } from '../../../context/FiltersContext';
import type { FiltersContext } from '../../../context/types';
import { type ConfigurableFilter, FILTER_TYPES, type FilterCriteria, type FilterValue } from '../../types';
import { getFilterButtonValue } from '../../utils';
import { FiltersBarButton } from '../FiltersBarButton';
import { FilterContentRenderer } from '../filterContent/FilterContentRenderer';
import { SingleFilterMenu } from './SingleFilterMenu';

type SingleFilterProps<Criteria extends FilterCriteria> = {
  filter: FiltersContext<Criteria>['filtersConfig'][number];
  options?: Option[];
};

export const SingleFilter = <Criteria extends FilterCriteria>({ filter, options }: SingleFilterProps<Criteria>) => {
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<HTMLElement | null>(null);
  const isInnerPopoverOpenRef = useRef(false);
  const { criteria, onFiltersChange, disabledFilters, isFilteringDisabled } = useFiltersContext<FilterCriteria>();
  const { key: filterKey, name, type, subName, clearable = true } = filter;
  const isDisabled = disabledFilters.includes(filterKey);
  const isClearable = clearable && !isDisabled;

  const pendingManagedUpdatesRef = useRef<Record<string, FilterValue | undefined> | null>(null);
  const flushScheduledRef = useRef(false);

  const flushManagedUpdates = useCallback(() => {
    if (!pendingManagedUpdatesRef.current) return;

    const updates = pendingManagedUpdatesRef.current;
    pendingManagedUpdatesRef.current = null;
    flushScheduledRef.current = false;

    const copied = structuredClone(criteria.filters || {});
    Object.entries(updates).forEach(([k, v]) => {
      if (v === undefined) {
        delete copied[k];
      } else {
        copied[k] = v as never;
      }
    });

    onFiltersChange(copied);
  }, [criteria.filters, onFiltersChange]);

  const buttonValue = useMemo(() => {
    if (filter.type === FILTER_TYPES.FORMAT_AND_DEVICES && filter.config.getButtonValue) {
      return filter.config.getButtonValue(criteria?.filters);
    }

    return getFilterButtonValue({ criteria: criteria?.filters, key: filterKey, options, type, filter });
  }, [criteria, filter, filterKey, options, type]);

  const handleOpenMenu = useCallback((e: MouseEvent<HTMLElement>) => {
    setFilterMenuAnchor(e.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setFilterMenuAnchor(null);
  }, []);

  const managedKeys =
    filter.type === FILTER_TYPES.FORMAT_AND_DEVICES ? (filter.config.managedKeys as string[]) : undefined;

  const handleClearFilter = useCallback(() => {
    if (!isClearable) return;

    if (managedKeys?.length) {
      const copiedFilters = structuredClone(criteria.filters || {});

      managedKeys.forEach(k => {
        if (copiedFilters[k] !== undefined) {
          delete copiedFilters[k];
        }
      });

      onFiltersChange(copiedFilters);
      return;
    }

    if (
      type === FILTER_TYPES.MULTI_OPTION_WITH_RADIO ||
      type === FILTER_TYPES.MULTI_OPTION_WITH_RANGE ||
      type === FILTER_TYPES.MULTI_SELECT_WITH_RADIO ||
      type === FILTER_TYPES.RANGE_WITH_RADIO
    ) {
      const copiedFilters = structuredClone(criteria.filters || {});

      options?.forEach(option => {
        if (copiedFilters[option.id]) {
          delete copiedFilters[option.id];
        }
      });

      onFiltersChange(copiedFilters);
      return;
    }

    onFiltersChange({
      ...criteria.filters,
      [filterKey]: undefined,
    });
  }, [
    criteria.filters,
    filterKey,
    onFiltersChange,
    isClearable,
    type,
    options,
    managedKeys?.forEach,
    managedKeys?.length,
  ]);

  const handleChangeFilters = useCallback(
    (key: string, options: FilterValue) => {
      const changedOptions = Array.isArray(options) ? (options.length > 0 ? options : undefined) : options;

      if (managedKeys?.includes(key)) {
        pendingManagedUpdatesRef.current = {
          ...(pendingManagedUpdatesRef.current ?? {}),
          [key]: changedOptions,
        };

        if (!flushScheduledRef.current) {
          flushScheduledRef.current = true;
          queueMicrotask(flushManagedUpdates);
        }

        return;
      }

      onFiltersChange({
        ...criteria.filters,
        [key]: changedOptions,
      });
    },
    [criteria.filters, onFiltersChange, managedKeys, flushManagedUpdates]
  );

  const handleMultiRangeChange = useCallback(
    (optionId: string, value: FilterValue) => {
      const updatedFilters = structuredClone(criteria.filters || {});

      if (value === undefined) {
        delete updatedFilters[optionId];
      } else {
        updatedFilters[optionId] = value;
      }

      onFiltersChange(updatedFilters);
    },
    [criteria.filters, onFiltersChange]
  );

  const changeHandler = useCallback(
    (type: ConfigurableFilter<keyof Criteria>['type']) => {
      if (type === FILTER_TYPES.MULTI_OPTION_WITH_RANGE || type === FILTER_TYPES.MULTI_OPTION_WITH_RADIO) {
        return handleMultiRangeChange;
      }

      return handleChangeFilters;
    },
    [handleMultiRangeChange, handleChangeFilters]
  );

  const isActive = !!buttonValue;

  const buttonIcon = useMemo(() => {
    if (!isActive || !isClearable) {
      return <ExpandMore aria-label="Show filter menu" />;
    }

    return (
      <Close
        aria-label="Close filter menu"
        data-test="close-single-filter-button"
        onClick={e => {
          e.stopPropagation();
          handleClearFilter();
        }}
      />
    );
  }, [isActive, isClearable, handleClearFilter]);

  if (type === FILTER_TYPES.SWITCH) {
    return (
      <FiltersBarButton
        isActive={isActive}
        disabled={isFilteringDisabled}
        hideOpenIcon
        disableRipple
        sx={{
          bgcolor: 'transparent',

          '&.MuiButton-root': {
            color: 'text.secondary',
          },
        }}
        onClick={e => {
          e.stopPropagation();
          if (isActive) {
            handleClearFilter();
          } else {
            const nextValue = options?.[0]?.id ?? '';

            handleChangeFilters(filterKey, nextValue);
          }
        }}
      >
        {type === FILTER_TYPES.SWITCH && (
          <FilterContentRenderer
            options={options}
            appliedFilters={criteria.filters}
            onOptionChange={changeHandler(type)}
            isDisabled={isDisabled}
            filter={filter}
            onInnerPopoverOpen={(open: boolean) => {
              isInnerPopoverOpenRef.current = open;
            }}
          />
        )}

        <Typography variant="body1Bold" component="span" sx={{ padding: 0, margin: 0 }}>
          {isActive ? `${name} ` : name}
        </Typography>
      </FiltersBarButton>
    );
  }

  return (
    <>
      <FiltersBarButton isActive={isActive} onClick={handleOpenMenu} disabled={isFilteringDisabled}>
        <Typography variant="body1Bold" component="span" sx={{ padding: 0, margin: 0 }}>
          {isActive ? `${name}: ` : name}
        </Typography>

        {isActive && (
          <Typography variant="body1Bold" component="span" sx={{ marginLeft: 0.5 }}>
            {buttonValue}
          </Typography>
        )}

        {!isDisabled && buttonIcon}
      </FiltersBarButton>

      <SingleFilterMenu
        anchorEl={filterMenuAnchor}
        open={!!filterMenuAnchor}
        onClose={handleCloseMenu}
        name={name}
        subName={subName}
        innerPopoverOpenRef={isInnerPopoverOpenRef}
      >
        <FilterContentRenderer
          options={options}
          appliedFilters={criteria.filters}
          onOptionChange={changeHandler(type)}
          isDisabled={isDisabled}
          filter={filter}
          onInnerPopoverOpen={(open: boolean) => {
            isInnerPopoverOpenRef.current = open;
          }}
        />
      </SingleFilterMenu>
    </>
  );
};
